import * as React from "react"
import { BiTrash } from "react-icons/bi"
import { FaUser } from "react-icons/fa"
import { ApolloQueryResult } from "@apollo/client"
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"

import { CommentItemFragment, GetProjectQuery, useDestroyCommentMutation } from "lib/graphql"
import { useMe } from "lib/hooks/useMe"
import { useMutationHandler } from "lib/hooks/useMutationHandler"

import { Confirmation } from "./Confirmation"
import { ProjectCommentsForm } from "./ProjectCommentsForm"

interface ProjectComments {
  projectId: string
  comments: CommentItemFragment[]
  commentsCount: number
  handleFetchMore: () => Promise<ApolloQueryResult<GetProjectQuery>> | undefined
  refetch: () => void
}
export const ProjectComments: React.FC<ProjectComments> = ({
  projectId,
  comments,
  commentsCount,
  handleFetchMore,
  refetch,
}) => {
  const colorGray = useColorModeValue("gray.500", "gray.400")
  const { me } = useMe()
  const handler = useMutationHandler()
  const [destroyComment, { loading: destroyCommentLoading }] = useDestroyCommentMutation()

  const handleDelete = (commentId: string) =>
    handler(() => destroyComment({ variables: { id: commentId } }), {
      onSuccess: (_, toast) => {
        refetch()
        toast({ description: "Opmerking verwijderd" })
      },
    })

  return (
    <>
      <ProjectCommentsForm projectId={projectId} refetch={refetch} />
      <Stack spacing={8} mt={8}>
        {comments.map((item) => {
          return (
            <HStack key={item.id} align="flex-start">
              <LinkBox position="relative" overflow="hidden">
                <LinkOverlay href={`/users/${item.user.id}`} textDecoration="none">
                  <Avatar
                    src={item.user.avatarUrl || undefined}
                    name={item.user.fullName || "avatar"}
                    w="45px"
                    h="45px"
                    bg="gray.600"
                    icon={<FaUser color="gray.900" size={18} />}
                    aria-label={`${item.user.fullName || "Gebruiker"} avatar`}
                  />
                </LinkOverlay>
              </LinkBox>
              <Stack spacing={0} flex={1}>
                <HStack>
                  <Text display="flex" flexDir="row" alignItems="center" color={colorGray} fontSize="sm">
                    {item.user.fullName}
                  </Text>
                  {item.isPrivate && (
                    <Badge ml={2} bgColor="pink.500" color="white" px={2}>
                      Privé
                    </Badge>
                  )}
                  {item.user.id === me?.id && (
                    <Confirmation
                      onSubmit={() => handleDelete(item.id)}
                      title="Verwijderen Comment"
                      text="Weet je het zeker? Dit kan niet ongedaan worden gemaakt."
                      submitText="Indienen"
                      submitColor="pink"
                    >
                      <IconButton
                        size="xs"
                        aria-label="commentaar verwijderen"
                        ml={2}
                        color="red"
                        variant="ghost"
                        icon={<Box as={BiTrash} />}
                        isLoading={destroyCommentLoading}
                        isDisabled={destroyCommentLoading}
                      />
                    </Confirmation>
                  )}
                </HStack>
                <Text>{item.message}</Text>
              </Stack>
            </HStack>
          )
        })}
      </Stack>
      {comments.length < commentsCount && (
        <Flex justifyContent="center">
          <Button onClick={handleFetchMore} mt={4} size="sm">
            Meer laden
          </Button>
        </Flex>
      )}
    </>
  )
}
